/* eslint-disable no-undef */
/* eslint-disable no-console */
import React, { useEffect, useState, Suspense } from 'react';
import { useIntl } from 'react-intl';
import { useRecoilValue, useSetRecoilState, useRecoilState } from 'recoil';
import { ThemeProvider, createTheme } from '@mui/material';
import { teal } from '@mui/material/colors';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import Navbar from './Desktop/Navbar';
import AccountSidebar from './Desktop/AccountSidebar';
import Add from './Desktop/Add';
import ProjectsView from './Desktop/ProjectsView';
import SidebarBox from './Desktop/SidebarBox';
import Login from './Shared/Login';
import Loading from './Shared/Loading';
import HomePageMobile from './Mobile/HomePageMobile';
import '../css/App.scss';
import useAPI from '../api';
import {
  loadingCounterAtom,
  projectsDataAtom,
  slideShowAtom,
  readOnlyAtom,
  userDataAtom,
  openProjectNameAtom,
  arrayChangesAtom,
  changesSendedAtom,
  enableChangesAtom,
  isChangedAtom,
  addNewPjAtom,
  newProjectObjAtom,
  pgmAreasAtom,
  pgTypesAtom,
  allPmsAtom,
  logoListAtom,
  selectedLogoAtom,
  blobLogoAtom,
  mobileAtom,
  logoUploadedAtom,
  editAtom,
  newEditedPjAtom,
  logoToSetAtom,
  utilisationsAtom,
} from './Atoms';
import CounterManager from '../api/CounterManager';
import { ROLES, DEFAULT_TIMEOUT } from '../Utils/constants';

const App = function App() {
  const ErrorDialog = React.lazy(() => import('./ErrorDialog'));
  const loadingCounter = useRecoilValue(loadingCounterAtom);
  const setSlideShow = useSetRecoilState(slideShowAtom);
  const setReadOnly = useSetRecoilState(readOnlyAtom);
  const teal700 = teal['700'];
  const teal400 = teal['400'];
  const API = useAPI();
  const matches = useMediaQuery('(min-width: 640px)');
  const setEnableChanges = useSetRecoilState(enableChangesAtom); //atom for enable changes from switch
  const [arrayChanges, setArrayChanges] = useRecoilState(arrayChangesAtom);
  const [changesSended, setChangesSended] = useRecoilState(changesSendedAtom);
  const setIsChanged = useSetRecoilState(isChangedAtom);
  const [user, setUser] = useState(null); //state that check login
  const [userData, setUserData] = useRecoilState(userDataAtom);
  const [projectsData, setProjectsData] = useRecoilState(projectsDataAtom);
  const [currentProjectData, setCurrentProjectData] = useState([]);
  const [copyData, setCopyData] = useState([]);
  const [reloadApi, setReloadApi] = useState(false);
  const [openProjectName, setOpenProjectName] = useRecoilState(openProjectNameAtom);
  const intl = useIntl();
  const [newProjectObj, setNewProjectObj] = useRecoilState(newProjectObjAtom);
  const [addNewPj, setAddNewPj] = useRecoilState(addNewPjAtom);
  const [add, setAdd] = useState(false);
  const [pgmAreas, setPgmAreas] = useRecoilState(pgmAreasAtom);
  const setPgTypes = useSetRecoilState(pgTypesAtom);
  const [allPms, setAllPms] = useRecoilState(allPmsAtom);
  const [logoList, setLogoList] = useRecoilState(logoListAtom);
  const [selectedLogo, setSelectedLogo] = useRecoilState(selectedLogoAtom);
  const [blobLogo, setBlobLogo] = useRecoilState(blobLogoAtom);
  const [mobile, setMobile] = useRecoilState(mobileAtom);
  const [openSidebar, setOpenSidebar] = useState(false);
  const [logoUploaded, setLogoUploaded] = useRecoilState(logoUploadedAtom);
  const [error, setError] = useState(null);
  const [editDialog] = useRecoilState(editAtom);
  const [newEditedPj, setNewEditedPj] = useRecoilState(newEditedPjAtom);
  const logoToSet = useRecoilValue(logoToSetAtom);
  const setUtilisations = useSetRecoilState(utilisationsAtom);
  /**Theme colors palette **/
  const theme = createTheme({
    name: 'green',
    palette: {
      primary: {
        main: teal700,
      },
      'p-green': {
        main: '#00796b',
      },
      secondary: {
        main: teal400,
      },
      error: {
        main: '#c83349',
      },
      variant: {
        main: '#a7fffa',
      },
      white: {
        main: '#ffffff',
      },
      grey: {
        main: '#ece7e7',
      },
      black: {
        main: '#111',
      },
      blackgrey: {
        main: '#7b7b7b',
      },
      secWhite: {
        main: '#c6e1de',
      },
    },
  });

  let closeCounter = 0;
  let pjCounter = 0;
  projectsData?.forEach((pj) => {
    if (pj.isCurrent === 1) {
      // eslint-disable-next-line no-plusplus
      pjCounter++;
      // eslint-disable-next-line no-plusplus
      if (pj.finishedPhase === 'RV7') closeCounter++;
    }
  });

  /**Filters data**/
  const applyFilters = (pjs, arFilters) => {
    const arrayFilters = [];
    pjs.forEach((project) => {
      let counter = 0;
      let filteredData = null;
      let pjStatus = 'open';
      if (project.finishedPhase === 'RV7') {
        pjStatus = 'close';
      }
      if (arFilters[0] && !project?.Name.includes(arFilters[0])) {
        // eslint-disable-next-line no-plusplus
        counter++;
      } else filteredData = project;
      if (counter === 0) {
        if (arFilters[1] && arFilters[1] !== filteredData?.Pm) {
          filteredData = null;
        }
      } else if (arFilters[1] && arFilters[1] === project?.Pm) {
        filteredData = null;
      }
      if (counter === 0) {
        if (arFilters[2] && arFilters[2] !== filteredData?.Client) {
          filteredData = null;
        }
      } else if (arFilters[2] && arFilters[2] === project?.Client) {
        filteredData = null;
      }
      if (counter === 0) {
        if (arFilters[3] && arFilters[3] !== pjStatus) {
          filteredData = null;
        }
      } else if (arFilters[3] && arFilters[3] === pjStatus) {
        filteredData = null;
      }
      if (filteredData) {
        arrayFilters.push(filteredData);
      }
    });
    return arrayFilters;
  };

  const handleReset = () => {
    setProjectsData(copyData);
  };
  /**Filtering data function**/
  const handleFilters = (filters) => {
    const filteredProjectsData = [...copyData];
    setProjectsData(applyFilters(filteredProjectsData, filters));
  };

  /**Update the project with the new version**/
  const handleNewVersion = (item) => {
    const copyItem = { ...item };
    const objWithLogo = Object.assign(copyItem, { LOGO: logoToSet });
    const index = projectsData.findIndex((x) => x.Name === copyItem.Name && x.isCurrent === 1);
    const newProjectsData = [...projectsData];
    newProjectsData.splice(index, 1, {
      ...newProjectsData[index],
      ...{ isCurrent: 0 },
    });
    setProjectsData([...newProjectsData, { ...objWithLogo }]);

    const indexCopy = copyData.findIndex((x) => x.Name === copyItem.Name && x.isCurrent === 1);
    const newCopyData = [...copyData];
    newCopyData.splice(indexCopy, 1, {
      ...newCopyData[indexCopy],
      ...{ isCurrent: 0 },
    });
    setCopyData([...newCopyData, { ...objWithLogo }]);
  };

  /**Update the projects page with the edited project**/
  const handleEditedProject = (item, name) => {
    if (blobLogo) {
      const itemCopyArray = [...item];
      const arrayWithLogo = [];
      const newProjectsData = [...projectsData];
      const newCopyData = [...copyData];
      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < itemCopyArray.length; i++) {
        arrayWithLogo.push(Object.assign(itemCopyArray[i], { LOGO: blobLogo }));
        const index = projectsData.findIndex(
          (x) =>
            x.Name === name &&
            x.idVersione === itemCopyArray[i].idVersione &&
            x.isCurrent === itemCopyArray[i].isCurrent
        );
        newProjectsData.splice(index, 1, {
          ...arrayWithLogo[i],
        });
        setProjectsData([...newProjectsData]);
        const indexCopy = copyData.findIndex(
          (x) =>
            x.Name === name &&
            x.idVersione === itemCopyArray[i].idVersione &&
            x.isCurrent === itemCopyArray[i].isCurrent
        );
        newCopyData.splice(indexCopy, 1, {
          ...arrayWithLogo[i],
        });
        setCopyData([...newCopyData]);
      }
    }
  };

  const handleNewProject = (item) => {
    if (blobLogo) {
      const copyItem = { ...item };
      const objWithLogo = Object.assign(copyItem, {
        LOGO: blobLogo,
        iFINECO: 0,
        iPRDLOG: 0,
        iTECIND: 0,
        iDOCQUA: 0,
        iHRCOMM: 0,
        eFINECO: 0,
        ePRDLOG: 0,
        eTECIND: 0,
        eDOCQUA: 0,
        eHRCOMM: 0,
        iFINECOnote: '',
        iPRDLOGnote: '',
        iTECINDnote: '',
        iDOCQUAnote: '',
        iHRCOMMnote: '',
        eFINECOnote: '',
        ePRDLOGnote: '',
        eTECINDnote: '',
        eDOCQUAnote: '',
        eHRCOMMnote: '',
      });
      const newProjectsData = [...projectsData];
      setProjectsData([...newProjectsData, { ...objWithLogo }]);
    }
  };

  useEffect(() => {
    if (!matches) {
      setMobile(true);
    } else setMobile(false);
  }, [matches]);

  useEffect(() => {
    if (selectedLogo) {
      API.getLogo(
        selectedLogo,
        (result) => {
          //console.log('setlogo e cose varie useffect setlogo');
          setBlobLogo(result);
        },
        (err) => {
          setError(err);
        }
      );
    }
  }, [selectedLogo, logoList]);

  useEffect(() => {
    if ((add || editDialog) && !allPms && !pgmAreas && logoList.length === 0) {
      API.getPms(
        (result) => {
          setAllPms(JSON.parse(result));
        },
        (err) => {
          setError(err);
        }
      );
      API.allPgmAreas(
        userData?.ruolo,
        (result) => {
          setPgmAreas(JSON.parse(result));
        },
        (err) => {
          setError(err);
        }
      );
      API.getLogoList(
        (result) => {
          setLogoList(JSON.parse(result));
        },
        (err) => {
          setError(err);
        }
      );
    }
  }, [add, editDialog]);

  useEffect(() => {
    if (logoUploaded)
      API.getLogoList(
        (result) => {
          setLogoList(JSON.parse(result));
          setLogoUploaded(false);
        },
        (err) => {
          setError(err);
        }
      );
  }, [logoUploaded]);

  useEffect(() => {
    if (newEditedPj && newProjectObj && projectsData) {
      const newObj = { ...projectsData.find((x) => x.Name === editDialog && x.isCurrent === 1) };
      const payloadObj = Object.assign(newObj, newProjectObj);
      API.editProjectRow(
        JSON.stringify([payloadObj, editDialog]),
        (result) => {
          setNewEditedPj(null);
          setNewProjectObj({});
          setSelectedLogo(payloadObj.LOGO);
          if (handleEditedProject) {
            handleEditedProject(JSON.parse(result), editDialog);
          }
        },
        (err) => {
          setError(err);
        }
      );
    }
  }, [newEditedPj]);

  useEffect(() => {
    if (addNewPj && newProjectObj) {
      API.addNewProject(
        JSON.stringify(newProjectObj),
        () => {
          setAddNewPj(false);
          setNewProjectObj({});
          setSelectedLogo(newProjectObj.LOGO);
          if (handleNewProject) handleNewProject(newProjectObj);
        },
        (err) => {
          setError(err);
        }
      );
    }
  }, [addNewPj]);

  useEffect(() => {
    setCurrentProjectData(projectsData?.find((x) => x.Name === openProjectName && x.isCurrent === 1));
  }, [openProjectName, projectsData]);

  useEffect(() => {
    const payloadData = { ...currentProjectData };
    if (changesSended && arrayChanges && arrayChanges.length > 0 && payloadData) {
      setChangesSended(false);
      setBlobLogo(null);
      //add changes to send to spreadsheet
      arrayChanges.forEach((obj) => {
        if (obj.id === 'finishedPhase') {
          payloadData[obj.id] = obj.title;
        } else if (!obj.id.includes('phase')) {
          payloadData[obj.id] = obj.value;
        }
      });
      payloadData.dataInizioValidita = new Date();
      payloadData.idVersione += 1;
      delete payloadData.LOGO;
      //send the current project with applied changes and creates a new version for the Stepper
      API.updateProjectRow(
        JSON.stringify(payloadData),
        () => {
          if (handleNewVersion) handleNewVersion(payloadData);
          setArrayChanges([]);
          setIsChanged(true);
          setEnableChanges(false);
        },
        (err) => {
          // eslint-disable-next-line no-console
          setError(err);
        }
      );
    }
  }, [changesSended]);

  useEffect(() => {
    if ((userData && userData?.id) || reloadApi) {
      API.getProjectsAutoUser(
        //return the logged user's projects
        (result) => {
          setProjectsData(JSON.parse(result));
          setCopyData(JSON.parse(result));
        },
        (err) => {
          // eslint-disable-next-line no-console
          setError(err);
        }
      );
    } else {
      setProjectsData([]);
    }
  }, [userData, reloadApi]);

  useEffect(() => {
    if (userData && userData?.id) {
      API.allPgTypes(
        (result) => {
          setPgTypes(JSON.parse(result));
        },
        (err) => {
          setError(err);
        }
      );
      API.allUtilisations(
        (result) => {
          setUtilisations(JSON.parse(result));
        },
        (err) => {
          setError(err);
        }
      );
    }
  }, [userData]);

  useEffect(() => {
    if (user) {
      API.getCurrentUserJson(
        //return the logged user data
        (result) => {
          //setLoadingCounter(loadingCounter - 1);
          setUserData(JSON.parse(result));
        },
        (err) => {
          // eslint-disable-next-line no-console
          //setLoadingCounter(loadingCounter - 1);
          setError(err);
        }
      );
    }
  }, [user]);

  const [currentProjectIndex, setCurrentProjectIndex] = useState(null);
  useEffect(() => {
    if (userData?.ruolo === ROLES.READER) {
      setReadOnly(true);
    }
    if (userData?.ruolo === ROLES.MONITOR) {
      if (currentProjectIndex == null) {
        setSlideShow(true);
        setCurrentProjectIndex(0);
      }
    }
  }, [projectsData]);

  useEffect(() => {
    if (currentProjectIndex !== null) {
      const projects = projectsData.filter((x) => x.isCurrent === 1);
      if (projects.length === 0 || currentProjectIndex > projects.length - 1) {
        setReloadApi(true);
        if (projects.length === 0) {
          // se non ci sono progetti attende che vengano ricaricati e dopo 10 secondi riprova
          setTimeout(() => setCurrentProjectIndex(0), DEFAULT_TIMEOUT);
        } else {
          setCurrentProjectIndex(0);
        }
      } else {
        setOpenProjectName(projects[currentProjectIndex].Name);
        setTimeout(() => {
          if (currentProjectIndex + 1 > projects.length) {
            setReloadApi(true);
            setCurrentProjectIndex(0);
          } else {
            setCurrentProjectIndex(currentProjectIndex + 1);
          }
        }, projects[currentProjectIndex].ShowSeconds * 1000);
      }
    }
  }, [currentProjectIndex]);

  const sidebar = (
    <AccountSidebar
      handleFilters={handleFilters}
      handleReset={handleReset}
      closeCounter={closeCounter}
      openCounter={pjCounter - closeCounter}
    />
  );
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <ThemeProvider theme={theme}>
        {!user && (
          <Grid container spacing={0}>
            <Grid
              sx={{
                backgroundColor: '#00796b',
                color: '#fff',
                height: '100vh',
                width: '100%',
              }}
            >
              <div
                style={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  paddingTop: '200px',
                }}
              >
                {mobile ? (
                  <Typography
                    sx={{ padding: '16px', textAlign: 'center', width: '50%', fontSize: '24px' }}
                    variant="h2"
                  >
                    {APP_NAME}
                  </Typography>
                ) : (
                  <Typography variant="h2">{APP_NAME}</Typography>
                )}
                <Typography variant="subtitle1" style={{ padding: '16px' }}>
                  {intl.messages.ACCESS_WITH_GOOGLE}
                </Typography>
                <Login setUser={setUser} />
              </div>
            </Grid>
          </Grid>
        )}
        {error && (
          <ErrorDialog
            error={error}
            handleClose={() => {
              setError(null);
              setUser(null);
            }}
          />
        )}
        {
          //check if user is logged to show or not the main page
          user && (
            <div>
              <Loading open={CounterManager.counter} loadingCounter={loadingCounter} />
              {!mobile ? (
                <div className="app">
                  <Navbar
                    title={APP_NAME}
                    setUser={setUser}
                    show={false}
                    setSidebar={() => setOpenSidebar(!openSidebar)}
                  />
                  <Grid container sx={{ width: '100vw' }}>
                    <div style={{ maxWidth: '280px', minWidth: '280px' }}>
                      <SidebarBox widget={sidebar} openSidebar={openSidebar} />
                    </div>
                    <div
                      style={{
                        height: '90%',
                        width: 'calc(100% - 280px)',
                        overflowY: 'auto',
                      }}
                    >
                      <ProjectsView />
                    </div>
                  </Grid>
                </div>
              ) : (
                <HomePageMobile setUser={setUser} />
              )}
              {(userData?.ruolo === 0 || userData?.ruolo === 1) && !mobile && (
                <Add add={add} setAdd={() => setAdd(!add)} />
              )}
            </div>
          )
        }
      </ThemeProvider>
    </Suspense>
  );
};

export default App;
